/**
 * PartellPage.tsx
 *
 * Account-wide Partell view: every Partell (RequestMyMeds) prescription across
 * all patients — Marketplace → Partell, the counterpart of Marketplace →
 * Axiom, built the same way: ListPageLayout + the shared Marketplace order
 * filters (resolved server-side, SCRUM-2207) + KPI tiles over the shared
 * IntegrationOrdersSubTab with a Patient column. Row
 * actions (pay, cancel, retry, re-order, clone, re-send, edit) and the
 * prescription form come from the same useAxiomOrderFlow the patient's Partell
 * tab uses, keyed to 'rmm'.
 */
import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FilePlus2, Megaphone } from 'lucide-react';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { StatCard } from '@/components/ui/StatCard';
import { OrderPatientPicker } from '@/components/orders/OrderPatientPicker';
import { MarketingActionsModal } from '@/components/marketing/MarketingActionsModal';
import { useAuth } from '@/hooks/useAuth';
import { useIntegrationOrderFilterOptions } from '@/hooks/filters';
import { buildApiParams, FILTER_KEYS } from '@/lib/filterRegistry';
import { useFacilityOverrideValues, useLocalDashboardFilters } from '@/pages/dashboard/dashboardFilters';
import {
  AWAITING_PAYMENT_STATUSES,
  INTEGRATION_ORDER_FILTER_CONFIGS,
  integrationOrderMarketingConditions,
  marketingListBlockedReason,
  NO_FACILITY,
  RMM_IN_FLIGHT_STATUSES,
  RMM_TILE_STATUSES,
  selectEquals,
} from '@/pages/marketplace/integrationOrdersShared';
import { IntegrationOrdersSubTab } from '@/pages/patients/labs/IntegrationOrdersSubTab';
import { useAxiomOrderFlow } from '@/pages/patients/labs/useAxiomOrderFlow';
import { RMM_FULFILLMENT_LABELS, RMM_STATUS_HELP } from '@/pages/patients/PartellTab';
import { getAccountIntegrationOrders, type IntegrationOrder } from '@/services/integrations.service';
import { listFacilities } from '@/services/admin.service';
import { computeSupersededBy, isDueForReorder } from '@/lib/peptideOrder';

const ALL_KEY = ['account-rmm-orders'];
const AWAITING_PAYMENT = AWAITING_PAYMENT_STATUSES;

export default function PartellPage() {
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [marketingModalOpen, setMarketingModalOpen] = useState(false);

  const filterState = useLocalDashboardFilters(INTEGRATION_ORDER_FILTER_CONFIGS, 'partell');
  const { effectiveValues, isFacilityLocked } = useFacilityOverrideValues(filterState.values);
  // Under a global facility override, orders with no facility stay reachable (as on Axiom).
  const pageValues = useMemo(() => {
    if (!isFacilityLocked) return effectiveValues;
    const ids = Array.isArray(effectiveValues.facility) ? effectiveValues.facility : [];
    return { ...effectiveValues, [FILTER_KEYS.FACILITY]: [...ids, NO_FACILITY] };
  }, [effectiveValues, isFacilityLocked]);

  // Every prescription: the KPI tiles' population.
  const { data: orders = [] } = useQuery({
    queryKey: ALL_KEY,
    queryFn: () => getAccountIntegrationOrders('rmm'),
    staleTime: 30_000,
  });
  // The table: through the page's filters, server-side (the same pipeline a
  // list saved from here resolves with).
  const apiParams = useMemo(
    () => buildApiParams(pageValues, filterState.negations),
    [pageValues, filterState.negations],
  );
  const { data: filtered = [], isLoading } = useQuery({
    queryKey: ['account-integration-orders', 'rmm', apiParams],
    queryFn: () => getAccountIntegrationOrders('rmm', apiParams),
    staleTime: 30_000,
    // Keep the rows on screen while a filter change refetches.
    placeholderData: (prev) => prev,
  });

  const flow = useAxiomOrderFlow({
    integrationKey: 'rmm',
    onRefresh: () => {
      void queryClient.invalidateQueries({ queryKey: ALL_KEY });
      void queryClient.invalidateQueries({ queryKey: ['account-integration-orders', 'rmm'] });
    },
  });
  const enabled = !!flow.axiom;
  const sidebarOptions = useIntegrationOrderFilterOptions({ integrationKey: 'rmm', statusLabels: RMM_FULFILLMENT_LABELS, ensureStatuses: RMM_TILE_STATUSES });

  // Export (SCRUM-2205): the Facility column is labelled from the facility list.
  const { data: facilities = [] } = useQuery({
    queryKey: ['admin-facilities'],
    queryFn: listFacilities,
    staleTime: 5 * 60_000,
  });
  const exportAs = useMemo(() => ({
    filePrefix: 'partell',
    facilityName: (id: number | null | undefined) =>
      id == null ? '' : (facilities.find((f) => f.facilityId === id)?.facilityName ?? ''),
  }), [facilities]);

  // KPI tiles: every prescription within the facility scope. Awaiting Payment
  // and In Fulfillment set the Status filter; Due for Re-order is a count.
  const facilityScope = Array.isArray(pageValues.facility) ? (pageValues.facility as string[]) : [];
  const facilityNegated = filterState.negations[FILTER_KEYS.FACILITY] === true;
  const matchesFacility = useCallback((o: IntegrationOrder) => {
    if (facilityScope.length === 0) return true;
    const inScope = facilityScope.includes(o.facilityId == null || o.facilityId === 0 ? NO_FACILITY : String(o.facilityId));
    // "Not facility X": the tiles count what the table lists.
    return facilityNegated ? !inScope : inScope;
  }, [facilityScope, facilityNegated]);
  // Due for Re-order counts exactly the rows that offer the Re-order button,
  // on the table's own rule (isDueForReorder: the reorderCountdown window,
  // finished, not dead, not already re-ordered) and the same unfiltered list,
  // so the tile, the filter and the amber row can never disagree.
  const supersededBy = useMemo(() => computeSupersededBy(orders, (o) => o.patientId ?? 0), [orders]);
  const isDue = useCallback(
    (o: IntegrationOrder) => isDueForReorder(o, supersededBy),
    [supersededBy],
  );
  const kpis = useMemo(() => {
    let total = 0; let awaitingPayment = 0; let inFlight = 0; let reorderDue = 0;
    for (const o of orders) {
      if (!matchesFacility(o)) continue;
      const fs = o.fulfillmentStatus ?? '';
      total += 1;
      if (fs === 'awaiting_payment') awaitingPayment += 1;
      if (RMM_IN_FLIGHT_STATUSES.includes(fs)) inFlight += 1;
      if (isDue(o)) reorderDue += 1;
    }
    return { total, awaitingPayment, inFlight, reorderDue };
  }, [orders, matchesFacility, isDue]);
  // Due for Re-order narrows the table on the page (it is computed from
  // supply, not a stored status); a list cannot keep it, so the button says so.
  const [reorderOnly, setReorderOnly] = useState(false);
  const tableOrders = useMemo(
    () => (reorderOnly ? filtered.filter(isDue) : filtered),
    [filtered, reorderOnly, isDue],
  );
  const listBlockedReason = marketingListBlockedReason(pageValues, { reorderDue: reorderOnly });
  const statusFilter = pageValues[FILTER_KEYS.ORDER_STATUS];
  const toggleStatusTile = useCallback((statuses: string[]) => {
    if (selectEquals(filterState.values[FILTER_KEYS.ORDER_STATUS], statuses)) filterState.clearFilter(FILTER_KEYS.ORDER_STATUS);
    else filterState.setFilter(FILTER_KEYS.ORDER_STATUS, statuses);
  }, [filterState]);

  const hasActiveFilters = reorderOnly || Object.entries(pageValues).some(([key, v]) => {
    if (isFacilityLocked && key === FILTER_KEYS.FACILITY) return false;
    if (Array.isArray(v)) return v.some((x) => String(x).trim() !== '');
    return !!(v && typeof v === 'object' && 'from' in v && (v.from || v.to));
  });

  // Row actions run against the order's own patient.
  const withPatient = (fn: (o: IntegrationOrder, patientId: number) => unknown) =>
    (o: IntegrationOrder) => { if (o.patientId) void fn(o, o.patientId); };

  return (
    <ListPageLayout
      filterSidebar={(
        <FilterSidebar
          filters={INTEGRATION_ORDER_FILTER_CONFIGS}
          values={effectiveValues}
          options={sidebarOptions}
          negations={filterState.negations}
          onChange={filterState.setFilter}
          onNegateChange={filterState.setNegation}
          onClear={filterState.clearFilter}
          lockedKeys={isFacilityLocked ? ['facility'] : undefined}
          searchCommitMode="blur-or-enter"
          showAIAssistant={false}
          storageKey="partell"
        />
      )}
    >
      <div className="flex h-full min-h-0 flex-col max-md:h-auto">
        <div className="shrink-0 space-y-6 p-6">
          <PageHeader
            variant="compact"
            title="Partell"
            subtitle="All Partell pharmacy prescriptions across your patients"
            actions={enabled && (
              <div className="flex items-center gap-2">
                {hasPermission(20) && (
                  <Button
                    size="sm"
                    variant="secondary"
                    leftIcon={<Megaphone className="h-4 w-4" />}
                    disabled={listBlockedReason !== null}
                    title={listBlockedReason ?? undefined}
                    onClick={() => setMarketingModalOpen(true)}
                  >
                    Email / SMS List
                  </Button>
                )}
                <Button size="sm" leftIcon={<FilePlus2 className="h-4 w-4" />} onClick={() => setPickerOpen(true)}>
                  New Prescription
                </Button>
              </div>
            )}
          />

          {enabled && (
            <MetricCardGrid columns={4}>
              <StatCard label="Total Orders" value={kpis.total} tone="blue" onClick={() => { filterState.clearFilter(FILTER_KEYS.ORDER_STATUS); setReorderOnly(false); }} tooltip="Show all statuses" />
              <StatCard label="Awaiting Payment" value={kpis.awaitingPayment} tone={kpis.awaitingPayment > 0 ? 'amber' : 'slate'} onClick={() => toggleStatusTile(AWAITING_PAYMENT)} selected={selectEquals(statusFilter, AWAITING_PAYMENT)} />
              <StatCard label="In Fulfillment" value={kpis.inFlight} tone="violet" onClick={() => toggleStatusTile(RMM_IN_FLIGHT_STATUSES)} selected={selectEquals(statusFilter, RMM_IN_FLIGHT_STATUSES)} />
              <StatCard label="Due for Re-order" value={kpis.reorderDue} tone={kpis.reorderDue > 0 ? 'amber' : 'slate'} onClick={() => setReorderOnly((on) => !on)} selected={reorderOnly} tooltip="Prescriptions whose supply runs out soon" />
            </MetricCardGrid>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-6 pb-6 max-md:overflow-visible">
          {!enabled ? (
            <p className="py-8 text-center text-sm text-slate-500">
              Partell is not enabled for this practice. Please contact your practice administrator to add it to your account.
            </p>
          ) : isLoading ? (
            <p className="py-8 text-center text-sm text-slate-500">Loading Partell prescriptions…</p>
          ) : (
            <IntegrationOrdersSubTab
              patientId={0}
              integrationKey="rmm"
              orders={tableOrders}
              allOrders={orders}
              showPatient
              exportAs={exportAs}
              fulfillmentLabels={RMM_FULFILLMENT_LABELS}
              statusHelp={RMM_STATUS_HELP}
              emptyMessage={hasActiveFilters ? 'No prescriptions match your filters.' : 'No Partell prescriptions yet.'}
              onFinishPayment={withPatient(flow.finishPayment)}
              onCancel={withPatient(flow.cancel)}
              onRetry={withPatient(flow.retry)}
              onEdit={withPatient(flow.edit)}
              onClone={withPatient(flow.clone)}
              onResend={withPatient(flow.resend)}
              onReorder={withPatient(flow.reorder)}
            />
          )}
        </div>
      </div>
      <OrderPatientPicker
        isOpen={pickerOpen}
        title="New Partell Prescription"
        onClose={() => setPickerOpen(false)}
        onSelect={(p) => { setPickerOpen(false); flow.startNewOrder(p.patientId); }}
      />
      {hasPermission(20) && marketingModalOpen && (
        <MarketingActionsModal
          isOpen
          onClose={() => setMarketingModalOpen(false)}
          sourceType="integration_order"
          listLabel="Partell"
          filterConditions={integrationOrderMarketingConditions('rmm', pageValues, filterState.negations)}
        />
      )}
      {flow.modals}
    </ListPageLayout>
  );
}
